import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin } from "lucide-react";
import seoData from "../data/seoPages.json";
import { useContent } from "../context/SiteContentContext";

export default function Footer() {
  const { get } = useContent();
  return (
    <footer className="relative border-t border-white/[0.05] mt-32 overflow-hidden" style={{ background: "#060606" }} data-testid="main-footer">
      {/* Subtle background glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "600px",
          height: "300px",
          background: "radial-gradient(ellipse, rgba(139,0,0,0.06) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-16">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-4 mb-6 group w-fit">
              <img
                src="/logo (2).png"
                alt="ROOM XIII"
                style={{
                  height: 64,
                  width: "auto",
                  filter: "drop-shadow(0 0 8px rgba(201,162,75,0.25))",
                  transition: "filter 0.35s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.filter = "drop-shadow(0 0 14px rgba(201,162,75,0.55))"}
                onMouseLeave={(e) => e.currentTarget.style.filter = "drop-shadow(0 0 8px rgba(201,162,75,0.25))"}
              />
              <div
                className="font-cormorant tracking-[0.28em]"
                style={{ fontSize: "1.6rem", fontWeight: 400 }}
              >
                <span className="text-gold">ROOM</span>
                <span className="text-crimson-bright"> XIII</span>
              </div>
            </Link>
            <p className="text-luxury-body text-sm max-w-sm">
              {get("site.footer_tagline")}
            </p>
            <div className="divider-gold" />
            <div className="flex gap-4 mt-1">
              <a
                href={get("site.instagram_url")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/30 hover:text-gold transition-colors"
                aria-label="instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={`mailto:${get("site.email")}`}
                className="text-white/30 hover:text-gold transition-colors"
                aria-label="email"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* Explore links */}
          <div>
            <div className="eyebrow mb-5">Explore</div>
            <ul className="flex flex-col gap-3 text-sm text-white/50">
              {[
                { to: "/room", label: "The Room" },
                { to: "/amenities", label: "Amenities" },
                { to: "/gallery", label: "Gallery" },
                { to: "/rules", label: "House Rules" },
                { to: "/about", label: "About Us" },
                { to: "/faq", label: "FAQ" },
                { to: "/cancellation-policy", label: "Cancellation Policy" },
                { to: "/policies", label: "Policies" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-gold transition-colors link-underline">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="eyebrow mb-5">Contact</div>
            <ul className="flex flex-col gap-4 text-sm text-white/50">
              <li className="flex items-start gap-3">
                <MapPin size={13} className="text-gold mt-1 shrink-0" />
                <span style={{ whiteSpace: "pre-line" }}>{get("site.address")}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={13} className="text-gold shrink-0" />
                <span>{get("site.phone")}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={13} className="text-gold shrink-0" />
                <span>{get("site.email")}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Local SEO links */}
        <div className="border-t border-white/[0.05] mt-16 pt-8">
          <div className="eyebrow mb-4">Stays Near You</div>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/40">
            {seoData.pages.map((p) => (
              <li key={p.slug}>
                <Link to={`/stays/${p.slug}`} className="hover:text-gold transition-colors">{p.keyword}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.05] mt-8 pt-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/25">
          <div>© {new Date().getFullYear()} ROOM XIII. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/policies" className="hover:text-gold transition-colors">Privacy</Link>
            <Link to="/policies" className="hover:text-gold transition-colors">Terms</Link>
            <Link to="/cancellation-policy" className="hover:text-gold transition-colors">Refund</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
